// This file is auto-generated. Do not edit manually.

import { z } from "zod";
import { ResponseMessageSchema, StatusSchema } from "./utilitySchemas";
import { EntitySchema } from "./entitySchemas";

export const RequestPriceListItemVisibilityEnum = z.enum(["DEFAULT", "FORM_ONLY"]);
export type RequestPriceListItemVisibility = z.infer<typeof RequestPriceListItemVisibilityEnum>;
export const PriceListInactiveReasonEnum = z.enum(["ARCHIVED"]);
export type PriceListInactiveReason = z.infer<typeof PriceListInactiveReasonEnum>;
export const PriceListQuerySchema = z.object({
  limit: z.coerce.number().int().positive().optional(),
  offset: z.coerce.number().int().nonnegative().optional(),
  sortBy: z.enum(["id", "name", "createdAt", "updatedAt"]).optional(),
  sortOrder: z.enum(["asc", "desc"]).optional(),
  extraService: z.enum(["true", "false"]).transform((val) => val === "true").optional(),
  filter: z
    .object({
      active: z
        .union([
          z.boolean(),
          z.enum(["true", "false"]).transform((val) => val === "true"),
        ])
        .optional(),
    })
    .optional(),
});
export type PriceListQuery = z.infer<typeof PriceListQuerySchema>;

export const ProductSchema = z.object({
  id: z.number(),
  name: z.string(),
  category: z.string(),
  defaultNetPrice: z.number().positive().nullable(),
  defaultGrossPrice: z.number().positive().nullable(),
  additional: z.string().optional().nullable(),
  priceType: z.string(),
  createdAt: z.string(),
});
export type Product = z.infer<typeof ProductSchema>;

export const SinglePriceListSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().optional(),
  active: z.boolean(),
  items: z.array(
    z.object({
      id: z.number(),
      price: z.number(),
      product: ProductSchema,
    })
  ),
});
export type SinglePriceList = z.infer<typeof SinglePriceListSchema>;

export const PriceListSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: SinglePriceListSchema,
});
export type PriceList = z.infer<typeof PriceListSchema>;

// New Schema
export const RequestPriceListSchema = z.object({
  id:z.string(),
  requestType: z.string(),
  additional: z.string().optional(),
  netPrice: z.number().positive(),
  grossPrice: z.number().positive(),
  nameEn: z.string(),
  nameDe: z.string(),
  additionalEn: z.string(),
  additionalDe: z.string(),
  isOther: z.boolean(),
  identifier: z.string().optional(),
  visibility: RequestPriceListItemVisibilityEnum,
  createdAt: z.string(),
  updatedAt: z.string().optional(),
});

export type RequestPriceList = z.infer<typeof RequestPriceListSchema>;

export const MarketValueFeesSchema = z.object({
  id: z.string(),
  value: z.number().nullable().optional(),
  fullNet: z.number().nullable().optional(),
  fullGross: z.number().nullable().optional(),
  shortNet: z.number().nullable().optional(),
  shortGross: z.number().nullable().optional(),
});

export type MarketValueFees = z.infer<typeof MarketValueFeesSchema>;

export const SqmRangeFeesSchema = z.object({
  id: z.string(),
  requestType: z.string(),
  minSqm: z.number().min(0),
  maxSqm: z.number().min(0).optional(),
  netPrice: z.number(),
  grossPrice: z.number(),
  createdAt: z.string(),
  updatedAt: z.string().optional(),
});

export type SqmRangeFees = z.infer<typeof SqmRangeFeesSchema>;

export const StripePriceListSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  active: z.boolean(),
  inactiveReason: PriceListInactiveReasonEnum.nullable().optional(),
  isExternal: z.boolean(),
  isExtraService: z.boolean(),
  isBasePriceList: z.boolean(),
  isBaseExtraService:z.boolean(),
  sqmRNDGPricingEnabled:z.boolean(),
  entities:z.array(EntitySchema),
  requestPriceLists:z.array(RequestPriceListSchema),
  marketValues: z.array(MarketValueFeesSchema),
  sqmRangeFees:z.array(SqmRangeFeesSchema),
  updatedAt: z.string(),
  createdAt: z.string(),
});

export type StripePriceList = z.infer<typeof StripePriceListSchema>;

export const StripePriceListResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.union([z.array(StripePriceListSchema),StripePriceListSchema]),
});
export type StripePriceListResponse = z.infer<typeof StripePriceListResponseSchema>;

export const SingleStripePriceListResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: StripePriceListSchema,
});

export const PriceListTableSchema = z.object({
  id: z.string(),
  product: z.string(),
  additional: z.string().nullable().optional(),
  gross: z.string().nullable().optional(),
  net: z.string().nullable().optional(),
  isSubRow: z.boolean().optional(),
  isGroupingRow: z.boolean().optional(),
});

export const DuplicateStripePriceListSchema = z.object({
  priceListId: z.string(),
  name: z.string(),
});

export type DuplicateStripePriceList = z.infer<typeof DuplicateStripePriceListSchema>;

export type PriceListTableData = z.infer<typeof PriceListTableSchema>;

export const UpdateRequestPriceListItemSchema = RequestPriceListSchema.partial();
export type UpdateRequestPriceListItem = z.infer<typeof UpdateRequestPriceListItemSchema>;

export const UpdateRequestMarketValueFeesSchema = MarketValueFeesSchema.partial();
export type UpdateRequestMarketValueFees = z.infer<typeof UpdateRequestMarketValueFeesSchema>;

export const UpdateRNDGFeesSchema = SqmRangeFeesSchema.partial();
export type UpdateRNDGFees = z.infer<typeof UpdateRNDGFeesSchema>;

export const UpdateStripePriceListSchema = z.object({
  priceListId: z.string(),
  name: z.string().optional(),
  description: z.string().optional(),
  requestPriceLists: z.array(UpdateRequestPriceListItemSchema).optional(),
  marketValues:z.array(UpdateRequestMarketValueFeesSchema).optional(),
  enableSqmRNDGPricing: z.boolean().optional(),
  rndgFees: z.array(UpdateRNDGFeesSchema).optional(),
});

export type UpdateStripePriceList = z.infer<typeof UpdateStripePriceListSchema>;


const PriceListUniquenessFieldEnum = z.enum(["name"]);
export type PriceListUniquenessField = z.infer<typeof PriceListUniquenessFieldEnum>;
export const CheckPriceListFieldUniquenessSchema = z.object({
  field: PriceListUniquenessFieldEnum,
  value: z.string().min(1, "Value cannot be empty"),
});
export type CheckPriceListFieldUniqueness = z.infer<typeof CheckPriceListFieldUniquenessSchema>;

export const CheckPriceListFieldUniquenessResponseSchema = z.object({
  status: StatusSchema,
  data: z.object({
    exists: z.boolean(),
  }),
});

export const PriceListArchiveBlockingReasonEnum = z.enum([
  "notFound",
  "inactive",
  "external",
  "basePriceList",
  "baseExtraServicesPriceList",
  "assignedToEntities",
]);
export type PriceListArchiveBlockingReason = z.infer<typeof PriceListArchiveBlockingReasonEnum>;

export const PriceListAssignmentTypeEnum = z.enum(["entity", "tempEntity"]);
export type PriceListAssignmentType = z.infer<typeof PriceListAssignmentTypeEnum>;

export const PriceListArchiveAssignmentSchema = z.object({
  type: PriceListAssignmentTypeEnum,
  id: z.number(),
  evalionId: z.string().nullable().optional(),
  name: z.string(),
  currentPriceListId: z.string(),
  currentPriceListName: z.string(),
});
export type PriceListArchiveAssignment = z.infer<typeof PriceListArchiveAssignmentSchema>;

export const PriceListArchiveEligibilitySchema = z.object({
  priceList: StripePriceListSchema.pick({
    id: true,
    name: true,
    active: true,
    inactiveReason: true,
    isExternal: true,
    isExtraService: true,
    isBasePriceList: true,
    isBaseExtraService: true,
  }),
  canArchive: z.boolean(),
  blockingReasons: z.array(PriceListArchiveBlockingReasonEnum),
  affectedAssignments: z.array(PriceListArchiveAssignmentSchema),
  affectedAssignmentCount: z.number().int().nonnegative(),
});
export type PriceListArchiveEligibility = z.infer<typeof PriceListArchiveEligibilitySchema>;

export const PriceListArchiveEligibilityResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema.optional(),
  data: PriceListArchiveEligibilitySchema,
});
export type PriceListArchiveEligibilityResponse = z.infer<typeof PriceListArchiveEligibilityResponseSchema>;

export const ReassignPriceListAssignmentsSchema = z.object({
  sourcePriceListId: z.string(),
  assignments: z.array(z.object({
    type: PriceListAssignmentTypeEnum,
    id: z.number().int(),
    replacementPriceListId: z.string(),
  })).min(1),
});
export type ReassignPriceListAssignments = z.infer<typeof ReassignPriceListAssignmentsSchema>;

export const ReassignPriceListAssignmentsResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema.optional(),
  data: z.object({
    reassignedCount: z.number().int().nonnegative(),
    eligibility: PriceListArchiveEligibilitySchema,
  }),
});
export type ReassignPriceListAssignmentsResponse = z.infer<typeof ReassignPriceListAssignmentsResponseSchema>;

export const ArchivePriceListSchema = z.object({
  priceListId: z.string(),
});
export type ArchivePriceList = z.infer<typeof ArchivePriceListSchema>;

export const ArchivePriceListResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema.optional(),
  data: StripePriceListSchema,
});
export type ArchivePriceListResponse = z.infer<typeof ArchivePriceListResponseSchema>;
